import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import PageHero from "../../components/public/PageHero";
import TurnstileField from "../../components/public/TurnstileField";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

export default function Contacto() {
  const { t, lang } = useLang();
  const [settings, setSettings] = useState(null);
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", subject: "", message: "", consent: false,
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/pages/contacto").then((r) => setPage(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setStatus({ loading: true, ok: null, msg: "" });
    try {
      await api.post("/contact", { ...form, turnstile_token: turnstileToken });
      setStatus({ loading: false, ok: true, msg: t("contact.success") });
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", consent: false });
      setTurnstileToken("");
    } catch (err) {
      setStatus({ loading: false, ok: false, msg: err.response?.data?.detail || t("contact.error") });
      setTurnstileToken(""); // Burn on failure.
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero title={t("contact.title")} subtitle={t("contact.subtitle")} />

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2 space-y-8">
          {settings && (
            <div className="space-y-6">
              {[
                { icon: MapPin, label: t("contact.address"), value: settings.contact_address },
                { icon: Phone, label: t("contact.phone"), value: settings.contact_phone, ltr: true },
                { icon: Mail, label: t("contact.email"), value: settings.contact_email, link: `mailto:${settings.contact_email}`, ltr: true },
                { icon: Clock, label: t("contact.hours"), value: settings[`contact_hours_${lang}`] || settings.contact_hours_es || settings.contact_hours_fr },
              ].map((it, i) => (
                <div key={i} className="flex items-start gap-4 pb-6 border-b border-gray-100">
                  <div className="w-10 h-10 bg-[#0F2040] text-white flex items-center justify-center shrink-0">
                    <it.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{it.label}</p>
                    {it.link ? (
                      <a href={it.link} {...(it.ltr && { dir: "ltr", style: { unicodeBidi: "isolate", textAlign: "start" } })} className="text-[#0F2040] font-medium hover:text-[#C8102E]">{it.value}</a>
                    ) : (
                      <p {...(it.ltr && { dir: "ltr", style: { unicodeBidi: "isolate", textAlign: "start" } })} className="text-[#0F2040] font-medium">{it.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          <h2 className="font-serif text-3xl text-[#0F2040] mb-8">{t("contact.formTitle")}</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <input
                required
                placeholder={t("contact.name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none"
                data-testid="contact-name"
              />
              <input
                required
                type="email"
                placeholder={t("contact.email")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none"
                data-testid="contact-email"
              />
              <input
                placeholder={t("contact.phone")}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none"
                data-testid="contact-phone"
              />
              <input
                placeholder={t("contact.company")}
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none"
                data-testid="contact-company"
              />
            </div>
            <input
              placeholder={t("contact.subject")}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none"
              data-testid="contact-subject"
            />
            <textarea
              required
              rows={6}
              placeholder={t("contact.message")}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none resize-none"
              data-testid="contact-message"
            />
            <label className="flex items-start gap-3 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5 accent-[#C8102E]"
                required
                data-testid="contact-consent"
              />
              <span>{t("contact.consent")}</span>
            </label>
            <TurnstileField action="contact" onToken={setTurnstileToken} />
            <button
              type="submit"
              disabled={status.loading}
              data-testid="contact-submit"
              className="bg-[#C8102E] hover:bg-[#A30D25] disabled:opacity-50 text-white px-8 py-3.5 text-sm font-medium tracking-wide transition-colors"
            >
              {status.loading ? t("contact.sending") : t("contact.send")}
            </button>
            {status.msg && (
              <p className={`text-sm ${status.ok ? "text-green-700" : "text-red-700"}`} data-testid="contact-status">
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </section>

      {settings?.map_embed && (
        <section className="border-t border-gray-100">
          <iframe
            src={settings.map_embed}
            className="w-full h-[420px] border-0"
            title="CECIT location"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </section>
      )}
    </div>
  );
}
